import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '~/lib/api-client';
import { operatorKeys } from '~/lib/query-keys';
import { describeOperatorMutationError } from './describe-operator-mutation-error';
import { OPERATOR_TAKEOVER_MUTATION_KEY } from '~/features/operator/use-operator-takeover-mutations-in-flight';
import type { OperatorRunWire } from '~/hooks/queries/use-operator-runs';

/**
 * FRONT-1996 "Take the wheel": the "Take over" button, in both the chat
 * card and the operator panel header. `POST /api/operator/runs/:runId/
 * takeover` is owner-scoped and idempotent on an already-ACTIVE takeover
 * (per `src/operator/store.ts`'s `startTakeover` — a second click is a
 * no-op, not a second audit event) but answers 409 for a run that has
 * already finished (`{error:"run_finished"}`) or has a stop already
 * pending (`{error:"stop_requested"}`) — real failures, never settled
 * optimistically, since a "you're in control" banner for a takeover that
 * never actually started would be a lie on screen while the person is
 * about to type into a desktop nobody is forwarding input to. Same
 * write-the-response-into-cache pattern as `useStopOperatorRun`, so the
 * card/panel reflect the new `takeover` state immediately rather than
 * waiting on the `operator-run-updated` SSE invalidation or the next 3s
 * poll tick.
 */
export function useTakeOverOperatorRun() {
  const qc = useQueryClient();
  return useMutation({
    // FRONT-1996 review M7: shared across every caller of this hook (the
    // chat card AND the panel each hold their own instance) so TanStack
    // itself — via `useIsMutating({ mutationKey: ... })`, fed into
    // `operatorTakeoverGate` — knows a takeover for THIS run is in flight
    // no matter which surface started it, rather than each surface only
    // knowing about its own click.
    mutationKey: OPERATOR_TAKEOVER_MUTATION_KEY,
    mutationFn: ({ runId }: { runId: string }) =>
      api.post<OperatorRunWire>(`/api/operator/runs/${encodeURIComponent(runId)}/takeover`, {}),
    // FRONT-1996 review M8: cancel any in-flight GET for this exact run
    // before the mutation's own request goes out — otherwise a poll
    // request that happens to resolve AFTER this mutation's response
    // writes the fresh state into the cache would silently overwrite it
    // with the stale pre-takeover snapshot it already had in flight.
    onMutate: async ({ runId }) => {
      await qc.cancelQueries({ queryKey: operatorKeys.run(runId) });
    },
    onSuccess: (run) => {
      qc.setQueryData(operatorKeys.run(run.id), run);
    },
    onError: (err, { runId }) => {
      toast.error(`Take over failed: ${describeOperatorMutationError(err)}`);
      // FRONT-1996 review M6: a real 409 (the run already finished, or a
      // stop is already pending) means the CACHED run this button was
      // rendered from is stale — refetch it so the UI stops offering a
      // control that will only fail again.
      qc.invalidateQueries({ queryKey: operatorKeys.run(runId) });
    },
  });
}
